import React, { useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdToken,
} from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { getFirebaseAuth } from '../lib/firebase';
import type { UserContext, OrganizationContext } from '../types/dashboard.types';
import { AuthContext } from './AuthContext';
import { logger } from '../utils/logger';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserContext | null>(() => {
    if (import.meta.env.VITE_MOCK_AUTH === 'true') {
      return {
        uid: import.meta.env.VITE_MOCK_USER_ID || 'user-acme-alice',
        email: 'alice@acme.com',
        role: 'customer_user',
      };
    }
    return null;
  });
  const [org, setOrg] = useState<OrganizationContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const performBackendSync = useCallback(async (firebaseUser: FirebaseUser) => {
    try {
      const idToken = await getIdToken(firebaseUser);
      const isExplicit = sessionStorage.getItem('explicit_login') === 'true';
      sessionStorage.removeItem('explicit_login');

      const response = await fetch(`${import.meta.env.VITE_BFF_URL || ''}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken, isExplicit }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Backend sync failed');
      }

      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        role: data.data.role || 'customer_user',
      });
      return true;
    } catch (err) {
      console.error('Failed to sync with backend:', err);
      const auth = getFirebaseAuth();
      await signOut(auth);
      setUser(null);
      setError('You do not have access to this portal. Please contact an administrator.');
      return false;
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_MOCK_AUTH === 'true') {
      setIsLoading(false);
      return;
    }

    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      setIsLoading(true);
      if (firebaseUser) {
        await performBackendSync(firebaseUser);
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [performBackendSync]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    // Explicit login: set flag in sessionStorage so we force a new session on BFF sync
    sessionStorage.setItem('explicit_login', 'true');
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      sessionStorage.removeItem('explicit_login');
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    try {
      const auth = getFirebaseAuth();
      await signOut(auth);
      setUser(null);
      setOrg(null);
      logger.info('User logged out');
      window.location.href = '/login';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError(message);
      throw err;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setOrgContext = (orgContext: OrganizationContext) => {
    setOrg(orgContext);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        org,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        logout,
        setOrgContext,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
